import React, { type ComponentType } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { ROUTE_PAY_AND_POST_V2 } from 'src/app/shared/constants/routes';
import { excludeKeys } from 'src/app/utils/excludeKeys';
import { log } from 'src/app/utils/logging';
import {
  getNextRouteByPageUid,
  getRouteByPageUid,
} from 'src/app/utils/pageUtils';
import { dispatchPageId } from 'src/app/v1/state/dispatcher/pageId';
import { actionUpdateProgress } from 'src/app/v1/state/modules/app/progress';
import { actionUpdateDraftProgress } from 'src/app/v1/state/modules/formData/draftProgress';
import { actionUpdatePreviousPageName } from 'src/app/v1/state/modules/tealium/previousPageName';
import { useLocalisedRoutes } from 'src/app/v1/views/hooks/useLocalisedRoutes';
import type { AppState } from 'src/types/appState';

export const withNextPageRoute =
  (WrappedComponent: ComponentType<any>) => (props: any) => {
    const { draftId, pageId, progress } = useSelector(
      (state: AppState) => ({
        draftId: state.session.draftId,
        pageId: state.session.pageId,
        progress: state.app.progress,
      }),
      shallowEqual,
    );

    const dispatch = useDispatch();
    const { goToRoute } = useLocalisedRoutes();

    const updateProgress = (
      currentPageId: string,
      isCurrentPageValid?: boolean,
    ) => {
      const newProgress = {
        currentPageId: progress.currentPageId,
        furthestPageId: progress.furthestPageId,
        isValidObj: {
          ...progress.isValidObj,
          [currentPageId]: isCurrentPageValid ?? true,
        },
      };

      dispatch(actionUpdateProgress(newProgress));
      dispatch(actionUpdateDraftProgress(newProgress.furthestPageId));
    };

    const handleNextPageRoute = () => {
      const nextRoutePageId = getNextRouteByPageUid(pageId);
      if (nextRoutePageId) {
        navigateTo(nextRoutePageId);
      }
    };

    const handleProgrammaticRouteChange = (
      nextRoutePageId: string,
      isValid?: boolean,
    ) => {
      log.info(`Navigate from ${pageId} to ${nextRoutePageId} page`);
      const nextRoute = getRouteByPageUid(nextRoutePageId);
      navigateTo(nextRoute, isValid);
    };

    const navigateTo = (nextRoute: string, isValid: boolean = true) => {
      dispatch(actionUpdatePreviousPageName(pageId));
      dispatchPageId(dispatch, nextRoute);
      updateProgress(pageId, isValid);

      if (nextRoute.includes(ROUTE_PAY_AND_POST_V2)) {
        const destination = draftId
          ? `${nextRoute}?draftid=${draftId}`
          : nextRoute;
        return goToRoute(destination);
      }

      return goToRoute(nextRoute);
    };

    const allProps = {
      ...props,
      handleNextPageRoute,
      handleProgrammaticRouteChange,
    };
    return <WrappedComponent {...excludeKeys(allProps, 'pageId')} />;
  };
